
import { supabase } from "@/integrations/supabase/client";

export interface Story {
  id: string;
  user_id: string;
  media_url: string;
  gif_preview_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  visibility: boolean;
  duration: number;
  media_type: string;
  rejection_reason?: string;
  location: { lat: number; lng: number };
  gif_crop_data?: {
    start_time: number;
    crop: { x: number; y: number; width: number; height: number };
  };
  created_at: string;
  expires_at: string;
  updated_at: string;
  user_name?: string;
  user_avatar?: string;
  distance_km?: number;
}

export const storiesService = {
  // Upload story video
  async uploadStory(file: File, location: { lat: number; lng: number }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const timestamp = Date.now();
    const fileName = `${user.id}/${timestamp}.mp4`;
    
    // Upload video to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('stories')
      .upload(fileName, file);
    
    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('stories')
      .getPublicUrl(fileName);

    // Create video element to get duration
    const videoDuration = await new Promise<number>((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });

    // Save story metadata
    const { data, error } = await supabase
      .from('stories')
      .insert({
        user_id: user.id,
        media_url: urlData.publicUrl,
        duration: videoDuration,
        location,
        media_type: 'video'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get stories sorted by distance
  async getStoriesByDistance(userLat: number, userLng: number, maxDistance = 100) {
    const { data, error } = await supabase.rpc('get_stories_by_distance', {
      user_lat: userLat,
      user_lng: userLng,
      max_distance_km: maxDistance
    });

    if (error) throw error;
    return data as Story[];
  },

  // Get user's own stories
  async getUserStories() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Story[];
  },

  // Upload and save GIF preview
  async uploadGifPreview(storyId: string, gifBlob: Blob, cropData: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const fileName = `${user.id}/story-preview.gif`;
    
    // Upload GIF to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('story-gifs')
      .upload(fileName, gifBlob, { upsert: true });
    
    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('story-gifs')
      .getPublicUrl(fileName);

    // Update story with GIF URL and crop data
    const { data, error } = await supabase
      .from('stories')
      .update({
        gif_preview_url: urlData.publicUrl,
        gif_crop_data: cropData
      })
      .eq('id', storyId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update story visibility
  async updateStoryVisibility(storyId: string, visibility: boolean) {
    const { data, error } = await supabase
      .from('stories')
      .update({ visibility })
      .eq('id', storyId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};
